import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import Logo from "../assets/logo.png";

/**
 * Splash de carga reutilizable. Se muestra mientras isLoading es true
 * y hace fade out al pasar a false.
 */
export default function SplashScreen({
	isLoading,
	message = "Preparando tu guía...",
}) {
	const [visible, setVisible] = useState(false);
	const [fading, setFading] = useState(false);

	useEffect(() => {
		if (isLoading) {
			setVisible(true);
			setFading(false);
		} else {
			setFading(true);
			const hideTimer = setTimeout(() => setVisible(false), 500);
			return () => clearTimeout(hideTimer);
		}
	}, [isLoading]);

	if (!visible) return null;

	return (
		<Box
			sx={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 99999,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				background:
					"linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)",
				opacity: fading ? 0 : 1,
				transition: "opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
				overflow: "hidden",
			}}
		>
			{/* Animated background shapes */}
			<Box
				sx={{
					position: "absolute",
					width: "100%",
					height: "100%",
					overflow: "hidden",
					"&::before": {
						content: '""',
						position: "absolute",
						top: "-50%",
						left: "-50%",
						width: "200%",
						height: "200%",
						background:
							"radial-gradient(circle at 30% 70%, rgba(99, 102, 241, 0.08) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(99, 102, 241, 0.06) 0%, transparent 50%)",
						animation: "splashRotate 20s linear infinite",
					},
					"@keyframes splashRotate": {
						"0%": { transform: "rotate(0deg)" },
						"100%": { transform: "rotate(360deg)" },
					},
				}}
			/>

			{/* Floating orbs */}
			<Box
				sx={{
					position: "absolute",
					width: 300,
					height: 300,
					borderRadius: "50%",
					background:
						"linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(99, 102, 241, 0.1) 100%)",
					filter: "blur(60px)",
					top: "10%",
					right: "-10%",
					animation: "splashFloat 4s ease-in-out infinite",
					"@keyframes splashFloat": {
						"0%, 100%": { transform: "translateY(0px) scale(1)" },
						"50%": { transform: "translateY(-30px) scale(1.05)" },
					},
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					width: 200,
					height: 200,
					borderRadius: "50%",
					background:
						"linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%)",
					filter: "blur(40px)",
					bottom: "15%",
					left: "5%",
					animation: "splashFloat 5s ease-in-out infinite reverse",
				}}
			/>

			{/* Main content */}
			<Box
				sx={{
					position: "relative",
					zIndex: 1,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 3,
				}}
			>
				{/* Logo with glow */}
				<Box
					sx={{
						position: "relative",
						animation: "splashPulse 2s ease-in-out infinite",
						"@keyframes splashPulse": {
							"0%, 100%": { transform: "scale(1)" },
							"50%": { transform: "scale(1.02)" },
						},
					}}
				>
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: 180,
							height: 180,
							borderRadius: "50%",
							background:
								"radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%)",
							filter: "blur(20px)",
						}}
					/>
					<img
						src={Logo}
						alt="Viajes Molones"
						style={{
							width: 160,
							height: "auto",
							position: "relative",
							zIndex: 1,
						}}
					/>
				</Box>

				{/* Modern animated loader */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mt: 2,
					}}
				>
					{[0, 1, 2, 3].map((i) => (
						<Box
							key={i}
							sx={{
								width: 8,
								height: 8,
								borderRadius: "50%",
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								animation: "splashDot 1.4s ease-in-out infinite",
								animationDelay: `${i * 0.15}s`,
								"@keyframes splashDot": {
									"0%, 80%, 100%": {
										transform: "scale(0.6)",
										opacity: 0.4,
									},
									"40%": {
										transform: "scale(1)",
										opacity: 1,
									},
								},
							}}
						/>
					))}
				</Box>

				{/* Subtle text */}
				<Typography
					sx={{
						mt: 1,
						fontSize: 13,
						fontWeight: 500,
						color: "#94a3b8",
						letterSpacing: "0.05em",
						animation: "splashFadeIn 1s ease-out",
						"@keyframes splashFadeIn": {
							"0%": { opacity: 0, transform: "translateY(10px)" },
							"100%": { opacity: 1, transform: "translateY(0)" },
						},
					}}
				>
					{message}
				</Typography>
			</Box>
		</Box>
	);
}
